import { Component, AfterViewInit } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { Input, HostBinding } from '@angular/core';
import { FormGroup } from '@angular/forms';

/************************************/
/* COMMON INTERFACE FOR VIEWS       */
/************************************/

/**
 * Represents a view for editing a product, providing methods to validate and handle user actions.
 */
export interface OnForm {
    /**
     * Determines whether the form can be accepted.
     * 
     * @returns {[boolean, boolean]} An array containing two boolean values:
     * 
     * - `true` if the form is valid and can be accepted; otherwise, `false`.
     * - `true` if the result is inconclusive and further validations should be performed.
     */    
    CanAccept(): boolean[];

    /**
     * Handles the aplication process.
     * 
     * @returns {Promise<boolean>} Resolves to `true` if the response is inconclusive,
     * allowing further validations to continue; otherwise, `false`.
     */
    OnApply(): Promise <boolean>;

    /**
     * Handles the acceptance process.
     * 
     * @returns {Promise<boolean>} Resolves to `true` if the response is inconclusive,
     * allowing further validations to continue; otherwise, `false`.
     */
    OnAccept(): Promise<boolean>;

    /**
     * Handles the cancellation process.
     * 
     * @returns {Promise<boolean>} Resolves to `true` if the response is inconclusive,
     * allowing further validations to continue; otherwise, `false`.
     */
    OnCancel(): Promise<boolean>;
}

/************************************/
/* UPP-FORM IMPLEMENTATION          */
/************************************/

@Component({
    selector: 'upp-form',
    templateUrl: './upp-form.html',
    styleUrls: ['./upp-form.scss'],
    styles: [`
        :host {
            display: block;
            height: 100%;
            min-height: 0;
            min-width: 0;
            width: 100%;

            form {
                position: relative;
                width: 100%;
                min-width: 0;
                height: 100%;
            }
        }
    `],
})
export class UppFormComponent {
    @Input() formGroup: FormGroup | null = null;

    /**
     * Vertical layout of the form.
     *
     * - `'auto'` (default): the form sizes to its content (current behavior).
     * - `'fill'`: the form becomes a vertical flex chain that stretches to fill its parent
     *   (host + inner `form` + `upp-scrollable`), so the scroll area grows and only the
     *   content scrolls. Consumers must not penetrate `form`/`upp-scrollable` with `::ng-deep`
     *   to obtain a full-height form.
     */
    @Input() layout: 'auto' | 'fill' = 'auto';

    /** Host class for the full-height variant (`layout === 'fill'`). */
    @HostBinding('class.--fill') get _fill(): boolean {
        return this.layout === 'fill';
    }

    /*
        Angular cannot proyect twice the ng-content, so we cannot have:

        <ng-container *ngIf="formGroup !== null">
            <ng-content></ng-content>
        </ng-container>
        <ng-container *ngIf="formGroup === null">
            <ng-content></ng-content>
        </ng-container>

        This is why, we neer a dummyForm, so that the form tag always has linked formGroup
    */
    private _dummyForm: FormGroup | null = null;
    get dummyForm(): FormGroup {
        if (!this._dummyForm){
            this._dummyForm = new FormGroup({});
        }
        return this._dummyForm;
    }
}

@Component({
    selector: 'upp-form-image',
    templateUrl: './parts/upp-image.html',
    styleUrls: ['./upp-form.scss'],
})
export class UppFormImageComponent {
    // nothing to do
}

@Component({
    selector: 'upp-form-section-end',
    template: `<ng-content></ng-content>`,
    styleUrls: ['./upp-form.scss'],
    host: {
        '[attr.slot]': "'end'",
    },
})
export class UppFormSectionEndComponent {
    // nothing to do
}

@Component({
    selector: 'upp-form-section',
    templateUrl: './parts/upp-section.html',
    styleUrls: ['./upp-form.scss'],
})
export class UppFormSectionComponent {
    @Input() title: string | null = null;
    @Input() subtitle: string | null = null;
    @Input() image: string | null = null;
    @Input() icon: string | null = null;
    /** Color temático del icono de cabecera (`upp-icon`); si es null, hereda del `ion-item`. */
    @Input() iconColor: string | null = null;
    @Input() slot: 'start' | 'end' = 'start';
    @Input() color: string | null = null;
    @Input() fill: 'clear' | 'solid' = 'solid';

    /** Fill token for tests and diagnostics (`--solid` / `--clear`). */
    get panelClasses(): string {
        return `--${this.fill}`;
    }

    /** Individual host classes so consumer `class` / `[class.*]` are not wiped. */
    @HostBinding('class.--solid') get _solidFill(): boolean {
        return this.fill === 'solid';
    }

    @HostBinding('class.--clear') get _clearFill(): boolean {
        return this.fill === 'clear';
    }
}

@Component({
    selector: 'upp-form-action',
    templateUrl: './parts/upp-action.html',
    styleUrls: ['./upp-form.scss'],
})
export class UppFormActionComponent {
    @Input() color: string | null = null;
    @Input() slot: 'start' | 'end' = 'start';
    @Input() icon: string | null = null;
    @Input() title: string | null = null;
    @Input() subtitle: string | null = null;
}

@Component({
    selector: 'upp-form-warning',
    templateUrl: './parts/upp-warning.html',
    styleUrls: ['./upp-form.scss'],
})
export class UppFormWarningComponent implements AfterViewInit {
    @Input() color: string | null = null;
    /**
     * Box style (`lines`):
     * - `'solid'`: bordered box with soft shadow; `color` tints the text.
     * - `'none'`: flush box (no radius/shadow/padding); `color` tints the text.
     * - `'fill'`: filled box; `color` is the background and text uses Ionic contrast.
     * - `'paper'`: white box, black border/text CTA for print/paper surfaces
     */
    @Input() lines: 'solid' | 'none' | 'fill' | 'paper' = 'solid'; 
    @Input() slot: 'start' | 'end' = 'start';
    @Input() icon: string | null = null;
    @Input() title: string | null = null;
    @Input() subtitle: string | null = null;

    @HostBinding('class') get panelClasses(): string {
        const borderClass = `--${this.lines}`;
        return `${borderClass}`.trim();
    }  

    private _visible = false;
    get Visible(): boolean {
        return this._visible;
    }

    set Visible(value: boolean){
        this._visible = value;
        this.change.markForCheck();
    }

    constructor(private change: ChangeDetectorRef){
        // nothing to do
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.Visible = true;
        }, 100);
    }
}

@Component({
    selector: 'upp-form-buttonbar',
    templateUrl: './parts/upp-buttonbar.html',
    styleUrls: ['./upp-form.scss'],
})
export class UppFormButtonBarComponent {
    // nothing to do
}

@Component({
    selector: 'upp-form-barbutton',
    templateUrl: './parts/upp-barbutton.html',
    styleUrls: ['./upp-form.scss'],
})
export class UppFormBarButtonComponent {
    @Input() active = true;
    @Input() color = 'success';
    @Input() disabled = false;
}

@Component({
    selector: 'upp-form-buttons',
    templateUrl: './parts/upp-buttons.html',
    styleUrls: ['./upp-form.scss'],
})
export class UppFormButtonsComponent {
    @Input() float: 'top' | 'bottom' = 'bottom';

    @HostBinding('class') get panelClasses(): string {
        const floatClass = `--${this.float}`;
        return `${floatClass}`.trim();
    }    
}